import { useEffect, useState } from 'react';
import { Heart, Activity, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { isChronicEnabled, forcedLifestyleMode } from '../config/appMode';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export default function LifestyleModeSelector() {
  const [selectedMode, setSelectedMode] = useState<'HEALTHY' | 'CHRONIC' | null>(null);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────
  // FII FIT (mode = FITNESS):
  // sărim acest pas complet. Salvăm automat 'HEALTHY' în backend și
  // redirecționăm utilizatorul la selectorul de obiectiv (Health Goal),
  // exact ca și cum ar fi apăsat butonul "Sunt sănătos".
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isChronicEnabled()) return; // în mod FULL afișăm ambele opțiuni
    const forced = forcedLifestyleMode() || 'HEALTHY';
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('accessToken');
        const response = await axios.put(
          `${API_URL}/api/auth/profile`,
          { lifestyleMode: forced },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      } catch (err) {
        console.error('[FII FIT] auto-set lifestyleMode failed:', err);
      } finally {
        window.location.href = '/health-goal';
      }
    })();
  }, []);

  const handleModeSelection = async (mode: 'HEALTHY' | 'CHRONIC') => {
    setSelectedMode(mode);
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        { lifestyleMode: mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update user in localStorage with COMPLETE updated user from backend
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Force page reload to refresh AuthContext with updated localStorage
      // Both modes now go to health goal selector first
      window.location.href = '/health-goal';
    } catch (error) {
      console.error('Error saving lifestyle mode:', error);
      alert('Eroare la salvarea preferinței. Te rog încearcă din nou.');
      setLoading(false);
    }
  };

  // În modul FITNESS arătăm doar un spinner cât timp se face redirect-ul
  if (!isChronicEnabled()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-700">Configurăm contul tău FII FIT...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Heart className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Alege modul tău de sănătate
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Personalizăm experiența în funcție de nevoile tale. Alege opțiunea care te descrie cel mai bine.
          </p>
        </div>

        {/* Mode Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* CHRONIC Mode */}
          <button
            onClick={() => handleModeSelection('CHRONIC')}
            disabled={loading}
            className={`relative bg-white rounded-2xl p-8 border-4 transition-all hover:shadow-2xl disabled:opacity-50 ${
              selectedMode === 'CHRONIC'
                ? 'border-blue-500 shadow-xl scale-105'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            {selectedMode === 'CHRONIC' && (
              <div className="absolute top-4 right-4">
                <CheckCircle className="w-8 h-8 text-blue-500" />
              </div>
            )}

            <div className="flex flex-col items-center text-center">
              {/* Icon */}
              <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
                <Heart className="w-12 h-12 text-white" />
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                💊 Am Afecțiune Cronică
              </h2>

              {/* Description */}
              <p className="text-gray-600 mb-6 leading-relaxed">
                Am o condiție medicală cronică (dermatomiozită, lupus, etc.) și doresc management personalizat.
              </p>

              {/* Features */}
              <div className="space-y-2 text-left w-full">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Planuri adaptate afecțiunii tale specifice</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Exerciții terapeutice personalizate</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Tracking medicamente și simptome</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Asistent AI medical specializat</span>
                </div>
              </div>

              {/* Badge */}
              <div className="mt-6 inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                Recomandare: Management Medical
              </div>
            </div>
          </button>
          {/* HEALTHY Mode */}
          <button
            onClick={() => handleModeSelection('HEALTHY')}
            disabled={loading}
            className={`relative bg-white rounded-2xl p-8 border-4 transition-all hover:shadow-2xl disabled:opacity-50 ${
              selectedMode === 'HEALTHY'
                ? 'border-green-500 shadow-xl scale-105'
                : 'border-gray-200 hover:border-green-300'
            }`}
          >
            {selectedMode === 'HEALTHY' && (
              <div className="absolute top-4 right-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            )}

            <div className="flex flex-col items-center text-center">
              {/* Icon */}
              <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
                <Activity className="w-12 h-12 text-white" />
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                🏃 Sunt Sănătos
              </h2>

              {/* Description */}
              <p className="text-gray-600 mb-6 leading-relaxed">
                Doresc să îmi îmbunătățesc stilul de viață, să slăbesc, să câștig masă musculară sau să mă mențin în formă.
              </p>

              {/* Features */}
              <div className="space-y-2 text-left w-full">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Planuri de slăbire/menținere/masă musculară</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Exerciții fitness pentru toate nivelurile</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Tracking nutriție și provocări zilnice</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">Asistent AI medical specializat</span>
                </div>
              </div>

              {/* Badge */}
              <div className="mt-6 inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Recomandare: Fitness & Wellness
              </div>
            </div>
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
          <p className="text-sm text-gray-600">
            💡 <strong>Nu te îngrijora!</strong> Poți schimba această preferință oricând din setări.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-700">Se salvează preferința...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
